import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

type Props = Record<string, never>;

@observer
export class AccessCodeEntry extends React.Component<Props> {
  @observable private _code = '';
  @observable private _status: 'idle' | 'loading' | 'cooldown' | 'error' = 'idle';
  @observable private _error = '';
  @observable private _cooldownSeconds = 0;

  private _cooldownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(props: Props) {
    super(props);
    makeObservable(this);
  }

  componentWillUnmount() {
    if (this._cooldownTimer) clearInterval(this._cooldownTimer);
  }

  render() {
    const isDisabled = this._status === 'loading' || this._status === 'cooldown';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <input
          type="text"
          className="form-control"
          placeholder="6-digit code"
          maxLength={6}
          value={this._code}
          onChange={this._onCodeChange}
          onKeyDown={this._onKeyDown}
          onFocus={this._onFocus}
          disabled={isDisabled}
          style={{ letterSpacing: '6px', fontFamily: 'monospace', fontSize: '20px', textAlign: 'center', width: '100%' }}
          autoComplete="off"
          inputMode="numeric"
        />
        <button
          className="btn btn-primary"
          onClick={this._onSubmit}
          disabled={isDisabled || this._code.length !== 6}
          style={{ width: '100%' }}
        >
          {this._status === 'loading' ? '…' : this._status === 'cooldown' ? `Wait ${this._cooldownSeconds}s` : 'Access File'}
        </button>
        {this._status === 'error' && (
          <div style={{ fontSize: '13px', color: 'var(--danger-color, #ef4444)', textAlign: 'center' }}>{this._error}</div>
        )}
      </div>
    );
  }

  @action.bound
  private _onFocus() {
    if (this._status === 'error') {
      this._status = 'idle';
      this._error = '';
    }
  }

  @action.bound
  private _onCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value.replace(/\D/g, '');
    this._code = val.slice(0, 6);
    if (this._status === 'error') {
      this._status = 'idle';
      this._error = '';
    }
  }

  @action.bound
  private _onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && this._code.length === 6) {
      this._onSubmit();
    }
  }

  @action.bound
  private async _onSubmit() {
    if (this._code.length !== 6 || this._status === 'loading' || this._status === 'cooldown') return;
    this._status = 'loading';
    this._error = '';

    try {
      const response = await fetch(`/api/access-code/${this._code}`);
      if (response.status === 429) {
        this._startCooldown(5);
        return;
      }
      if (response.status === 404) {
        this._status = 'error';
        this._error = 'Code not found or expired.';
        return;
      }
      if (!response.ok) {
        this._status = 'error';
        this._error = 'Something went wrong. Please try again.';
        return;
      }
      const data: { fileId: string; fileName: string; customName: string | null; behavior: 'download' | 'page' } = await response.json();
      if (data.behavior === 'download') {
        window.location.href = `/api/file/${data.fileId}/download`;
      } else {
        window.location.href = data.customName ? `/f/${data.customName}` : `/file/download/${data.fileId}`;
      }
    } catch {
      this._status = 'error';
      this._error = 'Network error. Please try again.';
    }
  }

  @action
  private _startCooldown(seconds: number) {
    this._status = 'cooldown';
    this._cooldownSeconds = seconds;
    this._cooldownTimer = setInterval(() => {
      if (this._cooldownSeconds <= 1) {
        if (this._cooldownTimer) clearInterval(this._cooldownTimer);
        this._cooldownTimer = null;
        this._status = 'idle';
        this._cooldownSeconds = 0;
      } else {
        this._cooldownSeconds -= 1;
      }
    }, 1000);
  }
}
